/**
 * analytics.js — buffered event tracking for the customer WEB app.
 *
 * Web port of the React Native analytics.js. Same enqueue/dedupe/batch-flush
 * contract, same event_type set, same `sendEvents` saga action — only the
 * platform-specific bits (foreground timing, device info) are swapped out.
 *
 * Pages import the track* helpers and call them freely:
 *
 *     import { trackImpression, trackClick, trackCouponView } from '.../analytics';
 *     trackImpression({ couponId, businessId, feedId, feedSort, feedPosition, dwellMs, eligible });
 *
 * These ENQUEUE events. They do NOT each fire a request. The buffer dedupes,
 * then flushes a batch (via the redux `sendEvents` action) on a timer, when
 * the buffer fills, or when the tab goes to background.
 *
 * SETUP (once, e.g. in your root App component / entry point after the store
 * exists):
 *
 *     import { initAnalytics } from '.../analytics';
 *     import { actions as statsActions } from '.../stats';
 *     initAnalytics({ dispatch: store.dispatch, sendEvents: statsActions.sendEvents });
 *
 * PAGE TRACKING (once per page, via useScreenTracking hook):
 *
 *     import { useScreenTracking } from '.../useScreenTracking';
 *     function CouponDetailsPage() {
 *       useScreenTracking('CouponDetails');
 *       ...
 *     }
 *
 * Every event enqueued after that automatically carries `screen: 'CouponDetails'`
 * until the next page sets a new one — no trackX() call site needs to pass it.
 *
 * DIFFERENCES FROM THE MOBILE VERSION (read before changing behaviour):
 *  - Foreground timing uses the Page Visibility API (`visibilitychange`)
 *    instead of AppState. `hidden` fires on tab switch, minimize, AND
 *    navigating away — the closest web analog to RN backgrounding — and
 *    unlike `unload`/`beforeunload` it reliably fires before the browser may
 *    tear the tab down, so it's also where we flush.
 *  - session_id lives in sessionStorage (not just an in-memory uuid), so a
 *    given browser tab keeps the same session across a client-side route
 *    change or a hard refresh, and only gets a new one when the tab actually
 *    closes/reopens — same intent as "one per app open" on mobile.
 *  - There's no react-native-device-info on web. `os` is a best-effort parse
 *    of navigator.userAgent; `device_model` is left null (no reliable web
 *    equivalent); `app_version` reads process.env.REACT_APP_VERSION — swap
 *    this for whatever your bundler's env convention is if you're not on
 *    CRA/react-scripts.
 *  - `pagehide` is wired as a last-resort second flush attempt, but same
 *    accepted limitation as mobile: a crashed/force-closed tab can still
 *    drop an unflushed buffer. Nothing here uses sendBeacon/fetch-keepalive —
 *    the flush still goes through the normal authApiCall/saga round trip, so
 *    it can still get cancelled mid-flight on an actual page teardown. Worth
 *    revisiting if you see gaps in the last-events-before-exit data.
 */
import { v4 as uuidv4 } from 'uuid';

// ---- tunables --------------------------------------------------------------
const FLUSH_INTERVAL_MS = 10000;  // flush at least every 10s
const FLUSH_AT_COUNT = 30;        // or once 30 events are buffered (<= backend max 200)
const MIN_FOREGROUND_MS = 500;    // ignore sub-500ms flickering visibility transitions
const SESSION_STORAGE_KEY = 'cb_analytics_session_id';
// const DEV = process.env.NODE_ENV !== 'production';
const DEV = false;

// ---- module state ----------------------------------------------------------
let _dispatch = null;
let _sendEvents = null;
let _sessionId = null;
let _buffer = [];
let _flushTimer = null;
let _flushing = false;
let _visibilityHandler = null;
let _pageHideHandler = null;

// Per-session dedupe sets
let _seenImpressions = new Set(); // `${feedId}:${couponId}`
let _seenViews = new Set();       // couponId

// pending coupon-detail view: { couponId, businessId, startedAt }
let _openCoupon = null;

// foreground timing: set when the tab becomes visible, null when hidden.
let _fgStart = null;

// current page name, set by useScreenTracking()/setCurrentScreen(). Not tied
// to session lifecycle — deliberately NOT reset by initAnalytics/resetSession,
// since whatever page is mounted stays "current" across a login/logout.
let _currentScreen = null;

// device/app metadata — read once in initAnalytics(). Static for the life of
// the tab, so unlike screen these are never reset by resetSession().
let _os = null;
let _deviceModel = null; // no reliable web equivalent — always null
let _appVersion = null;

// ---------------------------------------------------------------------------
// internal: device/app metadata
// ---------------------------------------------------------------------------
function _parseOs(userAgent) {
  if (!userAgent) return null;
  const ua = userAgent;
  const winMatch = ua.match(/Windows NT ([0-9.]+)/);
  if (winMatch) return 'windows ' + winMatch[1];
  const macMatch = ua.match(/Mac OS X ([0-9_]+)/);
  if (macMatch) return 'macos ' + macMatch[1].split('_').join('.');
  const iosMatch = ua.match(/(iPhone|iPad).*OS ([0-9_]+)/);
  if (iosMatch) return 'ios ' + iosMatch[2].split('_').join('.');
  const androidMatch = ua.match(/Android ([0-9.]+)/);
  if (androidMatch) return 'android ' + androidMatch[1];
  if (ua.indexOf('Linux') !== -1) return 'linux';
  return 'unknown';
}

function _captureDeviceInfo() {
  try {
    _os = _parseOs(navigator.userAgent);
    _deviceModel = null;
    _appVersion = process.env.REACT_APP_VERSION || null;
  } catch (err) {
    // Never let a device-info read failure block analytics init.
    if (DEV) console.warn('[analytics] device info capture failed:', err);
  }
}

function _getOrCreateSessionId() {
  try {
    const existing = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) return existing;
    const fresh = uuidv4();
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, fresh);
    return fresh;
  } catch (err) {
    // sessionStorage unavailable (private mode, disabled storage, etc) — fall
    // back to an in-memory-only id rather than failing analytics entirely.
    if (DEV) console.warn('[analytics] sessionStorage unavailable, using in-memory session id:', err);
    return uuidv4();
  }
}

// ---------------------------------------------------------------------------
// internal: foreground window recorder
// Called whenever the tab is hidden, on logout, and on teardown so no
// foreground time is ever silently discarded.
// ---------------------------------------------------------------------------
function _recordForegroundTime() {
  if (_fgStart === null) return;
  const durationMs = Date.now() - _fgStart;
  _fgStart = null; // clear so a double-hide doesn't double-count
  if (durationMs < MIN_FOREGROUND_MS) return; // ignore spurious flickers
//   enqueue('session_foreground', { duration_ms: durationMs });
}

// ---------------------------------------------------------------------------
// lifecycle
// ---------------------------------------------------------------------------
export function initAnalytics(options) {
  if (!options || !options.dispatch || !options.sendEvents) {
    console.error(
      '[analytics] initAnalytics() called without { dispatch, sendEvents } — analytics will not work until this is fixed. Got:',
      options,
    );
    return;
  }
  const { dispatch, sendEvents } = options;
  _dispatch = dispatch;
  _sendEvents = sendEvents;
  _sessionId = _getOrCreateSessionId();
  _buffer = [];
  _flushing = false;
  _seenImpressions = new Set();
  _seenViews = new Set();
  _fgStart = document.visibilityState === 'visible' ? Date.now() : null;

  _captureDeviceInfo();

  if (_flushTimer) clearInterval(_flushTimer);
  _flushTimer = setInterval(flush, FLUSH_INTERVAL_MS);

  if (_visibilityHandler) document.removeEventListener('visibilitychange', _visibilityHandler);
  _visibilityHandler = () => {
    if (document.visibilityState === 'hidden') {
      _recordForegroundTime();
      endCouponView();
      flush();
    } else if (document.visibilityState === 'visible') {
      _fgStart = Date.now();
    }
  };
  document.addEventListener('visibilitychange', _visibilityHandler);

  if (_pageHideHandler) window.removeEventListener('pagehide', _pageHideHandler);
  _pageHideHandler = () => {
    // best-effort second flush for the actual teardown, in case anything
    // slipped in between the visibilitychange flush and the real unload
    _recordForegroundTime();
    endCouponView();
    flush();
  };
  window.addEventListener('pagehide', _pageHideHandler);

  // console.log('[analytics] init | session', _sessionId, '| device', _os, _deviceModel, _appVersion);
}

export function teardownAnalytics() {
  // Record any outstanding foreground time before tearing down
  _recordForegroundTime();
  if (_flushTimer) clearInterval(_flushTimer);
  if (_visibilityHandler) document.removeEventListener('visibilitychange', _visibilityHandler);
  if (_pageHideHandler) window.removeEventListener('pagehide', _pageHideHandler);
  _flushTimer = null;
  _visibilityHandler = null;
  _pageHideHandler = null;
}

// Call on logout/login so a new session starts and the buffer doesn't carry over.
export function resetSession() {
  _recordForegroundTime(); // preserve the foreground window before resetting
  flush();
  _sessionId = uuidv4();
  try {
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, _sessionId);
  } catch (err) {
    if (DEV) console.warn('[analytics] could not persist new session id:', err);
  }
  _seenImpressions.clear();
  _seenViews.clear();
  _openCoupon = null;
  _fgStart = Date.now(); // new session is active from this moment
  // NOTE: os/device_model/app_version are intentionally left as-is — they're
  // properties of the tab/build, not the session, so there's nothing to reset.
}

// ---------------------------------------------------------------------------
// page tracking
// ---------------------------------------------------------------------------

// Sets the "current screen" attached to every event enqueued from now on.
// Usually you won't call this directly — use the useScreenTracking() hook
// instead, which wires it to route changes so it can't go stale.
export function setCurrentScreen(screenName) {
  _currentScreen = screenName;
  if (DEV) console.log('[analytics] screen ->', screenName);
}

export function getCurrentScreen() {
  return _currentScreen;
}

// ---------------------------------------------------------------------------
// internal: enqueue + flush
// ---------------------------------------------------------------------------
function enqueue(eventType, fields) {
  if (!_dispatch || !_sessionId) {
    if (DEV) console.warn('[analytics] enqueue before init:', eventType);
    return;
  }
  _buffer.push(Object.assign(
    {
      event_id: uuidv4(),
      ts: new Date().toISOString(),
      event_type: eventType,
      session_id: _sessionId,
      screen: _currentScreen,
      os: _os,
      device_model: _deviceModel,
      app_version: _appVersion,
    },
    fields,
  ));
  if (DEV) {
    console.log('[analytics] +' + eventType + ' | queue size ' + _buffer.length);
  }
  if (_buffer.length >= FLUSH_AT_COUNT) flush();
}

export function flush() {
  if (!_dispatch || !_sendEvents) {
    console.warn('[analytics] flush() called before initAnalytics() — nothing sent');
    return;
  }
  if (_flushing) {
    console.log('[analytics] flush() skipped — previous batch still in flight');
    return;
  }
  if (!_buffer.length) {
    return;
  }

  const batch = _buffer;
  _buffer = [];
  _flushing = true;

  console.log('[analytics] dispatching sendEvents — batch size', batch.length, batch);

  _dispatch(
    _sendEvents(batch, result => {
      _flushing = false;
      console.log('[analytics] sendEvents resolved —', result);
      if (!result || !result.success) {
        // server is idempotent (ON CONFLICT DO NOTHING), so re-sending is safe
        _buffer = batch.concat(_buffer);
      }
    }),
  );
}

// ---------------------------------------------------------------------------
// feed-level events
// ---------------------------------------------------------------------------
export function trackImpression({
  couponId,
  businessId,
  feedId,
  feedSort,
  feedPosition,
  dwellMs,
  eligible,
}) {
  // one impression per coupon per feed per session
  const key = feedId + ':' + couponId;
  if (_seenImpressions.has(key)) return;
  _seenImpressions.add(key);

  console.log('[analytics] trackImpression', { couponId, businessId, feedId, feedSort, feedPosition, dwellMs, eligible });

  enqueue('impression', {
    coupon_id: couponId,
    business_id: businessId,
    feed_id: feedId,
    feed_sort: feedSort,
    feed_position: feedPosition,
    dwell_ms: dwellMs,
    eligible,
  });
}

export function trackScrollPast({
  couponId,
  businessId,
  feedId,
  feedSort,
  feedPosition,
  dwellMs,
}) {
  enqueue('scroll_past', {
    coupon_id: couponId,
    business_id: businessId,
    feed_id: feedId,
    feed_sort: feedSort,
    feed_position: feedPosition,
    dwell_ms: dwellMs,
  });
}

export function trackClick({
  couponId,
  businessId,
  feedId,
  feedSort,
  feedPosition,
  dwellMs,
}) {
  enqueue('click', {
    coupon_id: couponId,
    business_id: businessId,
    feed_id: feedId,
    feed_sort: feedSort,
    feed_position: feedPosition,
    dwell_ms: dwellMs,
  });
}

// ---------------------------------------------------------------------------
// coupon-detail events
// ---------------------------------------------------------------------------
export function startCouponView({ couponId, businessId }) {
  _openCoupon = { couponId: couponId, businessId: businessId, startedAt: Date.now() };
}

export function endCouponView() {
  if (!_openCoupon) return;
  const couponId = _openCoupon.couponId;
  const businessId = _openCoupon.businessId;
  const startedAt = _openCoupon.startedAt;
  const durationMs = Date.now() - startedAt;
  _openCoupon = null;

  // one view per coupon per session
  if (_seenViews.has(couponId)) return;
  _seenViews.add(couponId);

  enqueue('coupon_view', {
    coupon_id: couponId,
    business_id: businessId,
    duration_ms: durationMs,
  });

  if (durationMs < 5000) {
    enqueue('quick_bounce', { coupon_id: couponId, business_id: businessId });
  }
}

export function trackDirectionsClick({ couponId, businessId }) {
  enqueue('directions_click', { coupon_id: couponId, business_id: businessId });
}

export function trackCallClick({ couponId, businessId }) {
  enqueue('call_click', { coupon_id: couponId, business_id: businessId });
}

export function trackProfileVisit({ couponId, businessId }) {
  enqueue('profile_visit', { coupon_id: couponId, business_id: businessId });
}

// ---------------------------------------------------------------------------
// affiliate events
// ---------------------------------------------------------------------------

// Fired when a user clicks through to an external affiliate destination.
// Not deduped — every click-through is a real intentional outbound action.
// Extra fields (network, merchant, url, surface) go into properties jsonb
// so no schema change is needed.
export function trackAffiliateClick({
  businessId = null,
  couponId = null,
  network = null,     // 'rakuten' | 'awin' | 'cj' | 'impact' | ...
  merchantId = null,  // affiliate_code / mid
  url = null,         // destination URL
  surface = null,     // 'business_visit_site' | 'deals_list' | 'flyer' | ...
}) {
  enqueue('affiliate_click', {
    coupon_id: couponId,
    business_id: businessId,
    properties: { network: network, merchant_id: merchantId, url: url, surface: surface },
  });
}

// ---------------------------------------------------------------------------
// session events
// ---------------------------------------------------------------------------

// session_foreground is emitted automatically by the visibilitychange
// listener above whenever the tab is hidden. You do not need to call
// anything from pages — it fires on its own. Each event records one
// continuous foreground window. Sum duration_ms per session_id for total
// foreground time per session.
//
// The function is exported only for testing purposes.
export { _recordForegroundTime as recordForegroundTime };